import { omit, pick, keys } from 'lodash';

import {
  jsUcFirst,
  removeNull,
  removeEmpty,
  replaceAllString,
  formatStringToUrl,
  recursiveFlatArray,
  formatMobileNumber,
  formatNumber,
  transformIncluded,
} from 'modules/common/utils';

import { renderTimeDuration } from './utils';

const marketplace_slug_suffix = process.env.REACT_APP_SLUG_SUFFIX || '';
const marketplace_hostname = process.env.REACT_APP_MARKETPLACE_HOSTNAME;

const merchantKey = {
  id: '',
  uuid: '',
  logo: '',
  slug: '',
  status: '',
  included: {},
  documents: '',
  full_name: '',
  first_name: '',
  bank_details: '',
  last_name: '',
  addresses: '',
  is_visible: '',
  profile_type: '',
  page_banner_url: '',
  business_logo: '',
  business_name: '',
  products_count: '',
  branches_count: '',
  email_address: '',
  mobile_number: '',
  formatted_mobile_number: '',
  business_category: [],
  business_category_codes: [],
  primary_payment_channel: '',
  primary_delivery_courier: '',
  business_facebook_account: '',
  business_instagram_account: '',
  business_twitter_account: '',
  delivery_couriers: '',
  payment_methods: '',
  payment_channels: '',
  walkthrough_step: {},
  changed_password_at: '',
  marketplace_url: '',
  has_pickup_options: '',
  subscription: {},
  distributor_uuid: '',
  crm_status: '',
  registered_at: '',
  onboarded_at: '',
  is_mfa_enabled: '',
  default_mfa: '',
  site: '',
};

const bankKey = {
  bank_uuid: {},
  branch: '',
  account_name: '',
  account_number: '',
  attachments: {
    file_url: '',
    file_name: '',
  },
};

export const transformBankDetails = (data) => {
  const args = {
    ...data,
    bank_uuid: {
      label: data?.bank_name,
      value: data?.bank_uuid,
    },
    attachments: {
      file_url: data?.file_url ?? '',
      file_name: data?.file_name ?? '',
    },
  };

  return { ...pick(removeNull(args), keys(bankKey)) };
};

const formatCategory = (list) => {
  const x = list?.map((item) => {
    const { uuid, title, parent_uuid } = item?.attributes;
    return {
      value: uuid,
      label: jsUcFirst(title),
      parent_uuid,
    };
  });

  const business_category = recursiveFlatArray(
    x,
    'value',
    'parent_uuid',
    'child'
  );

  return business_category;
};

const transformSubscription = (subscription, vendor_subscription) => {
  const {
    total_days = 0,
    remaining_days = 0,
    system_status = null,
  } = vendor_subscription?.attributes || {};
  const args = {
    ...omit(subscription, ['attributes', 'links']),
    ...subscription?.attributes,
    remaining_days,
    system_status,
    total_days,
  };

  return args;
};

const tranformDistributor = (data, subscription_plans) => {
  const {
    name,
    is_lifetime,
    expiration_days,
    subscription_parent,
    subscription_features,
  } = subscription_plans?.attributes || {};

  if (!data) return '';
  const x = {
    data: {
      features: subscription_features,
      custom_plan_name: name,
      custom_plan_duration:
        renderTimeDuration(is_lifetime, expiration_days) || '',
      subscription_type_name: subscription_parent?.name,
    },
    label: data?.attributes?.name || '',
    value: data?.attributes?.uuid || '',
  };

  return x;
};

export const transformMerchant = (data, included) => {
  const $data = transformIncluded(data || {}, included || []);
  const {
    slug,
    mobile_number,
    payment_methods,
    business_facebook_account,
    business_instagram_account,
    business_twitter_account,
  } = $data?.attributes || {};

  const {
    categories = [],
    distributor,
    subscription = {},
    vendorSubscription = {},
    pageBanner = {},
    user = {},
  } = $data?.included ?? {};

  const { is_mfa_enabled, default_mfa } = user?.attributes || {};

  const business_category = formatCategory(categories)?.map((item) => {
    const { child = [] } = item || {};
    return {
      level1: item,
      level2: child,
    };
  });

  const business_category_codes = categories
    .filter((x) => x?.attributes?.level === 1 && x?.attributes?.code)
    .map((x) => x?.attributes?.code);

  let args;

  try {
    args = {
      ...omit($data?.attributes, ['logo']),
      business_category,
      business_category_codes,
      distributor_uuid: tranformDistributor(distributor, subscription),
      bank_details: transformBankDetails($data?.attributes?.bank_details),
      full_name: $data?.attributes?.name,
      logo: $data?.attributes?.business_logo,
      page_banner_url: pageBanner ? pageBanner[0]?.attributes?.value : '',
      products_count: formatNumber($data?.attributes?.products_count, 0),
      branches_count: formatNumber($data?.attributes?.branches_count, 0),
      status: $data?.attributes?.status === 'Active',
      profile_type: {
        label: jsUcFirst($data?.attributes?.profile_type),
        value: $data?.attributes?.profile_type,
      },
      addresses: $data?.included?.addresses ?? [],
      documents: $data?.included?.documents ?? [],
      id: +$data?.id,
      walkthrough_step:
        $data?.included?.user?.attributes?.walkthrough_step ?? {},
      business_facebook_account: replaceAllString(
        business_facebook_account,
        'https://facebook.com/'
      ),
      business_instagram_account: replaceAllString(
        business_instagram_account,
        'https://instagram.com/'
      ),
      business_twitter_account: replaceAllString(
        business_twitter_account,
        'https://twitter.com/'
      ),
      marketplace_url: slug
        ? `https://${slug}${marketplace_slug_suffix}.${marketplace_hostname}`
        : '',
      payment_methods: Array.isArray(payment_methods)
        ? payment_methods
        : JSON.parse(payment_methods || null),
      subscription: transformSubscription(subscription, vendorSubscription),
      formatted_mobile_number: mobile_number ? `+${mobile_number}` : '--',
      crm_status: $data?.attributes?.crm_status || 'n/a',
      registered_at: $data?.attributes?.created_at || '-',
      onboarded_at: $data?.attributes?.onboarded_at || '-',
      is_mfa_enabled,
      default_mfa,
    };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log(err, ' here');
  }
  const x = { ...pick(removeNull(args), keys(merchantKey)) };

  return x;
};

export const transformPayload = (data) => {
  const {
    business_category,
    business_facebook_account,
    business_instagram_account,
    business_twitter_account,
    distributor_uuid,
  } = data;

  const x = {
    ...data,
    is_visible: +data?.is_visible,
    profile_type: data?.profile_type?.value ?? '',
    merchant_type: data?.merchant_type?.label ?? '',
    mobile_number: formatMobileNumber(data?.mobile_number),
  };
  return {
    ...removeEmpty(x),
    business_category:
      business_category?.map((item) => ({
        level1: item?.level1?.value,
        level2: item?.level2?.map((c) => c?.value),
      })) ?? [],
    tags: data?.tags?.map((item) => item?.value),
    business_facebook_account: business_facebook_account
      ? formatStringToUrl(`facebook.com/${business_facebook_account}`)
      : null,
    business_instagram_account: business_instagram_account
      ? formatStringToUrl(`instagram.com/${business_instagram_account}`)
      : null,
    business_twitter_account: business_twitter_account
      ? formatStringToUrl(`twitter.com/${business_twitter_account}`)
      : null,
    distributor_uuid: distributor_uuid?.value,
  };
};
